import { InputTextArea } from "@sellar-io/components";
import { useRef, useEffect, forwardRef } from "react";

export interface ChatInputProps {
  rows?: number;
  maxRows?: number;
  maxLength?: number;
  placeholder?: string;
  value: string;
  setValue: (value: string) => void;
  name?: string;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export const ChatInput = forwardRef<HTMLTextAreaElement, ChatInputProps>(
  (
    {
      rows = 1,
      maxLength,
      maxRows = 4,
      placeholder = "Type something...",
      value,
      setValue,
      name,
      onFocus,
      onBlur,
    },
    ref,
  ) => {
    const localRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      const textarea = localRef.current;
      if (textarea) {
        textarea.rows = rows;

        const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10);
        const newRows = Math.min(
          Math.max(rows, Math.floor((textarea.scrollHeight - 26) / lineHeight)),
          maxRows,
        );

        textarea.rows = newRows;
      }
    }, [value, maxRows, rows]);

    // Combine forwarded and local ref
    const setRefs = (el: HTMLTextAreaElement) => {
      if (typeof ref === "function") {
        ref(el);
      } else if (ref) {
        ref.current = el;
      }
      localRef.current = el;
    };

    return (
      <InputTextArea
        variant="legacyFixed"
        ref={setRefs}
        name={name}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        rows={1}
        maxLength={maxLength}
        className="h-auto w-full overflow-y-auto rounded-lg border px-4 py-[13px] leading-normal focus:border-zinc-800"
        placeholder={placeholder}
      />
    );
  },
);

ChatInput.displayName = "ChatInput";
